import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Shield, Scale, Award, AlertCircle, Camera, FileText, Heart, ChevronRight, BookOpen, ArrowRight, Clock, Calculator } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedPracticeAreas from '@/components/RelatedPracticeAreas';
import PracticeAreaFAQ from '@/components/PracticeAreaFAQ';

const slipFallFAQs = [
  {
    question: "How do I prove the property owner was negligent?",
    answer: "You need to show the owner knew (or should have known) about the dangerous condition and failed to fix it or warn you. Evidence like incident reports, security footage, maintenance logs, and witness statements are crucial—which is why quick action matters."
  },
  {
    question: "What if I fell in a store and they say it was my fault?",
    answer: "Property owners often blame victims for not watching where they were walking. However, stores have a duty to maintain safe premises. If a spill was left unattended or a hazard wasn't marked, the store may be liable regardless of their claims."
  },
  {
    question: "How long does the property owner have to fix a hazard?",
    answer: "Washington law requires 'reasonable' time to discover and address hazards. What's reasonable depends on the situation—a store should clean up spills within minutes, while a pothole might require days to repair. I analyze the specific facts of your case."
  },
  {
    question: "What compensation can I recover from a slip and fall?",
    answer: "You may recover medical expenses, lost wages, pain and suffering, and future medical care. Falls often cause serious injuries like fractures, head trauma, and back injuries that require extensive treatment and rehabilitation."
  },
  {
    question: "Should I report the fall to the property owner?",
    answer: "Yes, always report it and get documentation if possible. Ask for a copy of the incident report. Take photos of the hazard immediately. Get contact information from witnesses. This evidence is crucial for your claim."
  },
  {
    question: "How long do I have to file a slip and fall lawsuit in Washington?",
    answer: "Washington's statute of limitations is 3 years for most personal injury claims (RCW 4.16.080). However, claims against government entities have much shorter deadlines. Contact me promptly to protect your rights."
  }
];

const SlipFallPage = () => {
  // HowTo schema for "What to Do After a Slip and Fall"
  const howToSteps = {
    name: "What to Do After a Slip and Fall Accident in Washington",
    description: "Essential steps to protect your rights and build a strong premises liability claim after falling on someone else's property.",
    totalTime: "PT30M",
    steps: [
      { name: "Stay Calm and Assess Your Injuries", text: "Don't rush to get up. Take a moment to assess your injuries. Moving too quickly could worsen a back, neck, or head injury." },
      { name: "Report the Incident Immediately", text: "Notify the property owner, manager, or employee about your fall. Request they create a written incident report and get a copy." },
      { name: "Document the Hazardous Condition", text: "Take photos or video of what caused your fall—wet floor, torn carpet, broken stairs, ice, poor lighting. Capture the area before it's cleaned or fixed." },
      { name: "Gather Witness Information", text: "Get names and contact information from anyone who saw your fall or saw the hazardous condition before the accident." },
      { name: "Seek Medical Attention", text: "Get medical care as soon as possible, even if injuries seem minor. Medical records establish a clear link between your fall and your injuries." },
      { name: "Preserve Your Clothing and Shoes", text: "Keep the clothes and shoes you were wearing. They may serve as evidence and can refute claims that your footwear caused the fall." },
      { name: "Don't Give Recorded Statements", text: "Do not sign anything or give recorded statements to the property owner's insurance company without legal advice." },
      { name: "Contact a Slip and Fall Attorney", text: "An experienced premises liability attorney can investigate the property owner's negligence and fight for fair compensation for your injuries." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="slip-fall-page">
      <SEOHead 
        title="Slip and Fall Attorney | Premises Liability | Amy C. Brown Law"
        description="Experienced slip and fall lawyer handling premises liability claims in Washington. Holding property owners accountable for unsafe conditions that cause injuries."
        serviceData={{
          name: "Slip and Fall Legal Representation",
          description: "Premises liability representation for slip and fall injuries caused by property owner negligence, including wet floors, uneven surfaces, and inadequate lighting.",
          category: "Premises Liability"
        }}
        howToData={howToSteps}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Slip and Falls
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              It's important that after a slip and fall, you protect your rights and prove liability. I can help secure fair compensation for medical costs and lost wages.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="slip-fall-hero-call"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Slip and Falls */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Slip and Fall Injuries Can Be Serious
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Slip and fall accidents may seem minor, but they can result in serious injuries including broken bones, head injuries, back and spinal injuries, and hip fractures. Property owners have a duty to maintain safe premises, and when they fail in that duty, you may be entitled to compensation.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Proving liability in slip and fall cases requires demonstrating that the property owner knew or should have known about the dangerous condition and failed to address it. With my experience on both sides of personal injury cases, I know how to build a strong case for you.
              </p>
              
              <div className="bg-accent-soft rounded-xl p-6">
                <h3 className="font-semibold text-secondary mb-4">What You May Be Entitled To:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </span>
                    <span className="text-slate-700">Medical expenses</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </span>
                    <span className="text-slate-700">Lost wages</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </span>
                    <span className="text-slate-700">Pain and suffering</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </span>
                    <span className="text-slate-700">Future medical care</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Shield className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Prove Liability</h4>
                <p className="text-sm text-slate-600">I know how to establish property owner negligence</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Scale className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">No Fees Unless You Win</h4>
                <p className="text-sm text-slate-600 mb-2">Contingency fee basis</p>
                <Link to="/pricing" className="inline-flex items-center gap-1 text-teal-600 text-sm font-medium hover:text-teal-700">
                  See pricing <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Award className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">10+ Years Experience</h4>
                <p className="text-sm text-slate-600">Proven track record</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Heart className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Personal Attention</h4>
                <p className="text-sm text-slate-600">Work directly with me</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Do After */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-12">
            What to Do After a Slip and Fall
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-teal-600 font-bold text-2xl">1</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Seek Medical Attention</h3>
              <p className="text-slate-600 text-sm">Get checked by a doctor even if injuries seem minor—some injuries don't show symptoms immediately.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Camera className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Document Everything</h3>
              <p className="text-slate-600 text-sm">Take photos of the hazard, your injuries, and the location. Get witness contact information.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Report the Incident</h3>
              <p className="text-slate-600 text-sm">File an incident report with the property owner or manager. Keep a copy for your records.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-secondary mb-3">Contact an Attorney</h3>
              <p className="text-slate-600 text-sm">Call me before speaking to insurance companies. I'll protect your rights from day one.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Hazards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-12">
            Common Slip and Fall Hazards
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              'Wet or slippery floors',
              'Uneven surfaces or broken sidewalks',
              'Poor lighting',
              'Missing handrails',
              'Cluttered walkways',
              'Icy or snowy conditions',
              'Loose carpeting or rugs',
              'Defective stairs',
              'Parking lot potholes'
            ].map((hazard, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                <span className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs">✓</span>
                </span>
                <span className="text-slate-700">{hazard}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statute Warning */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <AlertCircle className="w-16 h-16 text-teal-400 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Take Timely Action to Protect Your Case
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-8">
            Do not let precious time slip away—acting swiftly is critical to protect your case. Evidence can vanish, and memories of witnesses may fade. With a <a href="https://apps.leg.wa.gov/RCW/default.aspx?cite=4.16.080" target="_blank" rel="noopener noreferrer" className="text-teal-300 underline hover:text-white font-medium">three-year statute of limitations</a> for most personal injury claims in Washington, I urge you to contact me today where my compassion and expertise can help develop a strategy to win your case.
          </p>
        </div>
      </section>

      {/* Helpful Resources - Internal Linking */}
      <section className="py-16 bg-slate-50" data-testid="slip-fall-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Learn More
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Resources for Slip & Fall Victims
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Understand premises liability and what you need to prove for a successful claim.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/what-to-do-after-car-accident-washington"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <FileText className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                What to Do After an Accident
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Document the scene, report the incident, and protect your rights from day one.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/blog/why-statutes-of-limitations-exist-washington-personal-injury"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Clock className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                Filing Deadlines in Washington
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Learn about the 3-year statute of limitations and exceptions that may apply.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/compensation-estimator"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Calculator className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                Estimate Your Compensation
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Use our free calculator to see what your slip and fall case may be worth.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Try Calculator <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PracticeAreaFAQ
        title="Slip and Fall FAQs"
        subtitle="Common questions about premises liability and slip and fall claims in Washington."
        faqs={slipFallFAQs}
        accentColor="primary"
        schemaId="slip-fall"
      />

      {/* Related Practice Areas */}
      <RelatedPracticeAreas currentSlug="slip-and-falls" />

      {/* Case Result blurb — slip and fall case study */}
      {/* HIDDEN — Case study pages are temporarily hidden pending review.
      <section className="py-14 md:py-16 bg-slate-50 border-t border-slate-100" data-testid="slip-fall-case-result-blurb">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-teal-700 font-semibold mb-3">
            <Award className="w-4 h-4" /> Case Result
          </div>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
                Six-Figure Settlement for Dominant-Wrist Fracture from Wet-Floor Slip and Fall
              </h2>
              <p className="text-slate-600 leading-relaxed">
                A slip on a wet floor at a business caused a dominant-wrist fracture requiring permanent hardware. Amy secured a six-figure settlement without filing a lawsuit.
              </p>
            </div>
            <Link
              to="/case-results/restaurant-slip-fall-wrist-fracture"
              data-testid="slip-fall-case-result-link"
              className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-primary/90 transition whitespace-nowrap"
            >
              Read the Case Study <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Every case is different. Past results do not guarantee similar outcomes.
          </p>
        </div>
      </section>
      */}

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation - No Obligation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
              data-testid="slip-fall-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-6 h-6" />
              Text (425) 675-1224
            </a>
          </div>
          <p className="mt-6 text-teal-200 text-sm">
            Page last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default SlipFallPage;
